import history from '@AppRoutes/history';
import Auth from '@Services/Auth';
import PendoService from '@Services/Pendo';
import { PENDO_URL } from '@Constants/BuildInfo';
import AppStorage from '@Services/AppStorage';
import HttpRequest from '@Services/RequestFactory';
import loginConstants from '@Services/Login/LoginConstants';
import Catalyst from '@Services/Catalyst';
import NavigationHistory from '@Services/NavigationHistory';
import pick from 'lodash/pick';
import extend from 'lodash/extend';
import find from 'lodash/find';

import RequestUrls from '@Constants/RequestUrlsConstants';
import MessageBroker from '@Services/MessageBroker';
import { storeInstitutionRememberMe, removeInstitutionRememberMe } from '@Pages/Login/Institution/institutionRememberMeUtils';

import {
  institutionSetActualDataReducerType,
  institutionSetInstitutionsForSearchReducerType,
  institutionSetInstitutionsGroupsReducerType,
  institutionSetIpAddressReducerType,
  institutionSetIsLoadingReducerType,
  institutionSetNotificationsReducerType,
  institutionResetStateReducerType,
} from './InstitutionConstants';

import { actualData as actualDataSelector, institutionsGroups as institutionsGroupsSelector } from './InstitutionSelectors';

// Parse each group in response, loop through institutions and add new field "group" with the description of group
// To be able to select and store correct institution
function preMapInstitutionsResponse(response) {
  return response.map((group) => {
    const institutions = group.institutions?.map((institution) => ({ ...institution, group: group.description }));
    return { ...group, institutions };
  });
}

function getCallbackUrl(institution) {
  if (!institution) return null;

  let url = HttpRequest.getUrlWithPrefix('/oauth/shibboleth-sso');
  if (url.charAt(0) === '/') {
    url = window.location.origin + url;
  }
  return institution.externalUrl + encodeURIComponent(url);
}

export function setIsLoading(isLoading) {
  return {
    type: institutionSetIsLoadingReducerType,
    payload: { isLoading },
  };
}

export function setIpAddress(ipAddress) {
  return {
    type: institutionSetIpAddressReducerType,
    payload: { ipAddress },
  };
}

export function setInstitutionsForSearch(institutionsForSearch) {
  return {
    type: institutionSetInstitutionsForSearchReducerType,
    payload: { institutionsForSearch },
  };
}

export function setInstitutionsGroups(institutionsGroups) {
  return {
    type: institutionSetInstitutionsGroupsReducerType,
    payload: { institutionsGroups },
  };
}

export function setNotifications(notifications) {
  return {
    type: institutionSetNotificationsReducerType,
    payload: { notifications },
  };
}

export function setActualData(actualData) {
  return {
    type: institutionSetActualDataReducerType,
    payload: { actualData },
  };
}

export function cancel() {
  return async () => {
    const stateWithParams = NavigationHistory.getLastVisitedState(...loginConstants.STATE_WHITELIST);
    if (stateWithParams) {
      history.push({
        pathname: stateWithParams.pathName,
        state: stateWithParams.params,
      });
    } else {
      Auth.goToLandingPage();
    }
  };
}

export function login() {
  return async (dispatch, getState) => {
    dispatch(setIsLoading(true));
    const actualData = actualDataSelector(getState());

    if (actualData.institution && actualData.callbackUrl) {
      // save last selected institution
      AppStorage.instance.set('lastSelectedInstitution', pick(actualData, 'institution', 'callbackUrl', 'group'));
    }
    if (actualData.institution && actualData.rememberChoice) {
      // Save remember me institution info
      AppStorage.instance.set('userInstitutionInfo', {
        rememberChoice: actualData.rememberChoice,
        institution: actualData.institution,
        group: actualData.group,
        callbackUrl: actualData.callbackUrl,
      });
      // Save remember me choice for session
      storeInstitutionRememberMe(true);
    } else {
      AppStorage.instance.remove('userInstitutionInfo');
      removeInstitutionRememberMe();
    }

    window.location.href = actualData.callbackUrl;
  };
}

export function getInstitutionsForSearch() {
  return async (dispatch, getState) => {
    const institutionsGroups = institutionsGroupsSelector(getState());

    institutionsGroups.forEach((group) => {
      if (group.institutions && group.institutions.length) {
        dispatch(setInstitutionsForSearch(group.institutions));
      }
    });
  };
}

export const init = () => async (dispatch) => {
  const notifications = Auth.getUserNotifications();
  dispatch(setNotifications(notifications));

  Catalyst.trackSimpleEvent('loginStart');

  await PendoService.link(PENDO_URL)
    .then(() => PendoService.updateVisitor())
    .catch(Logger.warn);

  const userData = AppStorage.instance.get('userInstitutionInfo');
  const isRemember = userData && userData.rememberChoice;

  await MessageBroker.send(RequestUrls.institutions, null, { Authorization: false })
    .then(async (data) => {
      // eslint-disable-next-line no-param-reassign
      data = preMapInstitutionsResponse(data);
      dispatch(setInstitutionsGroups(data));
      dispatch(getInstitutionsForSearch());
      if (isRemember) {
        // Get current group from remember me: try to match by description only
        const currentGroup = find(data, (group) => group.description === userData.group);

        if (!currentGroup) return;

        // Get current institution from remember me - match by description
        const currentInstitution = find(currentGroup?.institutions, (item) => item.description === userData.institution);

        if (!currentInstitution) return;

        dispatch(
          setActualData({
            group: currentGroup.description,
            institution: currentInstitution.description,
            rememberChoice: userData.rememberChoice,
            callbackUrl: getCallbackUrl(currentInstitution),
            currentInstitutions: currentGroup.institutions?.length ? currentGroup.institutions : [],
          })
        );
      }
    })
    .catch(Logger.error);

  await Auth.getClientInfo()
    .then((data) => {
      dispatch(setIpAddress(data.ipAddress));
    })
    .catch(Logger.error);
};

export function goToLastSelected(lastSelectedInstitution) {
  return async (dispatch, getState) => {
    dispatch(setIsLoading(true));

    const actualData = actualDataSelector(getState());

    // Save remember me choice for session
    if (actualData.rememberChoice) {
      AppStorage.instance.set('userInstitutionInfo', {
        rememberChoice: actualData.rememberChoice,
        ...lastSelectedInstitution,
      });
      storeInstitutionRememberMe(true);
    } else {
      AppStorage.instance.remove('userInstitutionInfo');
      removeInstitutionRememberMe();
    }

    window.location.href = lastSelectedInstitution?.callbackUrl;
  };
}

export function selectGroup(group) {
  return async (dispatch, getState) => {
    const actualData = actualDataSelector(getState());

    dispatch(
      setActualData({
        ...actualData,
        group: group?.description,
        institution: '',
        currentInstitutions: group?.institutions.length ? group.institutions : [],
      })
    );
  };
}

export function selectInstitution(institution, lastSelected) {
  return async (dispatch, getState) => {
    const actualData = actualDataSelector(getState());

    /** this is from autosuggest */
    if (lastSelected) {
      extend(lastSelected, {
        institution: institution.description,
        group: institution.group,
        callbackUrl: getCallbackUrl(institution),
      });
      return;
    }

    /** this is from dropdown */
    dispatch(
      setActualData({
        ...actualData,
        institution: institution.description,
        callbackUrl: getCallbackUrl(institution),
      })
    );
  };
}

export function selectSearchedInstitution(institution) {
  return async (dispatch, getState) => {
    dispatch(setIsLoading(true));
    const lastSelected = {};
    await dispatch(selectInstitution(institution, lastSelected));
    AppStorage.instance.set('lastSelectedInstitution', lastSelected);

    const actualData = actualDataSelector(getState());
    // Save remember me choice for session
    if (actualData.rememberChoice) {
      AppStorage.instance.set('userInstitutionInfo', {
        rememberChoice: actualData.rememberChoice,
        institution: institution.description,
        group: institution.group,
        callbackUrl: getCallbackUrl(institution),
      });
      storeInstitutionRememberMe(true);
    } else {
      AppStorage.instance.remove('userInstitutionInfo');
      removeInstitutionRememberMe();
    }

    window.location.href = getCallbackUrl(institution);
  };
}

export function changeRememberMe(value) {
  return async (dispatch, getState) => {
    const actualData = actualDataSelector(getState());

    dispatch(
      setActualData({
        ...actualData,
        rememberChoice: value,
      })
    );
  };
}

export function resetState() {
  return {
    type: institutionResetStateReducerType,
  };
}
